import { useNavigate, useLocation } from 'react-router-dom';
import { type ConnectionStatus } from '../types/gateway';
import { type UserRole } from '../types/gateway';
import type { SessionInfo } from '../types/gateway';
import { RoleSwitcher } from './RoleSwitcher';
import { SessionMenu } from './SessionMenu';
import styles from './TopBar.module.css';

interface TopBarProps {
  connectionStatus: ConnectionStatus;
  currentRole: UserRole;
  onRoleChange: (role: UserRole) => void;
  sessions?: SessionInfo[];
  activeSessionId?: string | null;
  onCreateSession?: () => void;
  onSwitchSession?: (id: string) => void;
  onToggleSidebar?: () => void;
}

export function TopBar({
  connectionStatus,
  currentRole,
  onRoleChange,
  sessions = [],
  activeSessionId = null,
  onCreateSession,
  onSwitchSession,
  onToggleSidebar,
}: TopBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isTraining = location.pathname === '/training';

  const statusColor =
    connectionStatus === 'connected'
      ? 'var(--green)'
      : connectionStatus === 'connecting'
        ? 'var(--amber)'
        : 'var(--red)';

  const statusText =
    connectionStatus === 'connected'
      ? 'Gateway Connected'
      : connectionStatus === 'connecting'
        ? 'Connecting...'
        : 'Disconnected';

  return (
    <header className={styles.topBar}>
      {onToggleSidebar && (
        <button
          className={styles.hamburger}
          onClick={onToggleSidebar}
          aria-label="Toggle sidebar"
        >
          &#9776;
        </button>
      )}

      <div className={styles.brand}>
        <span className={styles.brandName}>HOLLER</span>
        <span className={styles.brandSub}>powered by <strong>FOREMAN</strong></span>
      </div>

      <button
        className={`${styles.navLink} ${isTraining ? styles.navLinkActive : ''}`}
        onClick={() => navigate(isTraining ? '/' : '/training')}
      >
        {isTraining ? 'Chat' : 'Training'}
      </button>

      {onCreateSession && onSwitchSession && (
        <SessionMenu
          sessions={sessions}
          activeSessionId={activeSessionId}
          onCreateSession={onCreateSession}
          onSwitchSession={onSwitchSession}
        />
      )}

      <div className={styles.status}>
        <span className={styles.statusDot} style={{ background: statusColor }} />
        <span className={styles.statusText}>{statusText}</span>
      </div>

      <RoleSwitcher currentRole={currentRole} onRoleChange={onRoleChange} />
    </header>
  );
}
